import {
  ago,
  ConfidenceReportSection,
  correctionReasons,
  date,
  money,
  quantity,
  signed,
} from "@lumberset/takeoff-ui";
import {
  Badge,
  Container,
  EmptyState,
  ProgressBar,
  SectionHeader,
  Skeleton,
  Stat,
  StatusBadge,
  Table,
  type Tone,
} from "@lumberset/ui";
import { ChartActivity } from "@medusajs/icons";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Crumbs } from "../../components/crumbs";
import {
  accuracyConfidenceQuery,
  accuracyOverviewQuery,
  accuracyRunQuery,
  accuracyRunsQuery,
  accuracyScoresQuery,
} from "../../lib/queries";
import { agreementQuery, agreementReviewQuery } from "../../lib/queries-lessons";

/** The window the tiles cover, and the longer one the run list and the agreement cover. */
const DAYS = 30;
const RUN_DAYS = 90;
const AGREEMENT_DAYS = 90;

export const Route = createFileRoute("/_app/accuracy")({
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(accuracyOverviewQuery(DAYS)),
      queryClient.ensureQueryData(accuracyRunsQuery(RUN_DAYS)),
      queryClient.ensureQueryData(accuracyScoresQuery),
      queryClient.ensureQueryData(agreementQuery(AGREEMENT_DAYS)),
    ]),
  component: Accuracy,
});

const percent = (share: number | null | undefined) =>
  share === null || share === undefined ? "-" : `${Math.round(share * 100)}%`;

const statusTone: Record<string, Tone> = { released: "green", in_review: "orange" };
const statusLabel: Record<string, string> = { released: "Released", in_review: "In review" };

const categoryLabel: Record<string, string> = {
  walls: "Walls",
  plate_heights: "Plate heights",
  openings: "Openings",
  roof: "Roof",
  floors: "Floors",
  beams: "Beams",
  sheathing: "Sheathing",
  hardware: "Hardware",
  sheet_index: "Sheet index",
};

const scoreTone = (score: number): Tone =>
  score >= 0.9 ? "green" : score >= 0.7 ? "orange" : "red";

function Accuracy() {
  const { data: overview } = useSuspenseQuery(accuracyOverviewQuery(DAYS));
  const { data: runs } = useSuspenseQuery(accuracyRunsQuery(RUN_DAYS));
  const { data: scores } = useSuspenseQuery(accuracyScoresQuery);
  const [selected, setSelected] = useState<string | null>(null);

  const mostCorrected = overview.correctionsByCollection[0]?.count ?? 0;
  const handChecked = scores.reports.filter((report) => report.truthKind !== "reviewed");
  const planSets = new Set(handChecked.map((report) => report.planSet)).size;
  const reviewedReports = scores.reports.length - handChecked.length;

  return (
    <>
      <Crumbs trail={[{ label: "Accuracy" }]} />

      <Container className="grid grid-cols-2 gap-px overflow-hidden bg-ui-border-base p-0 lg:grid-cols-3">
        <Stat
          className="bg-ui-bg-base"
          label="Runs read"
          value={overview.runsRead}
          hint={`Last ${DAYS} days, real readings only`}
        />
        <Stat
          className="bg-ui-bg-base"
          label="Median cost"
          value={overview.medianCostCents === null ? "-" : money(overview.medianCostCents)}
          hint={
            overview.medianMinutes === null
              ? "Model spend per run"
              : `Model spend per run, ${overview.medianMinutes} minutes each`
          }
        />
        <Stat
          className="bg-ui-bg-base"
          label="Held for review"
          value={percent(overview.gatedShare)}
          hint="Share of runs held before release"
        />
        <Stat
          className="bg-ui-bg-base"
          label="Released untouched"
          value={percent(overview.releasedWithoutCorrectionsShare)}
          hint="Released with no corrections at all"
        />
        <Stat
          className="bg-ui-bg-base"
          label="Corrections per run"
          value={overview.correctionsPerRun ?? "-"}
          hint="Elements a reviewer or member changed"
        />
        <Stat
          className="bg-ui-bg-base"
          label="Harness reports"
          value={scores.reports.length}
          hint={`Readings scored against ${planSets} hand checked plan set${planSets === 1 ? "" : "s"}${reviewedReports > 0 ? `, ${reviewedReports} against reviewed truth` : ""}`}
        />
      </Container>

      <AgreementSection />

      <ConfidenceSection />

      <Container className="divide-y p-0">
        <SectionHeader
          title="What reviewers change"
          description={`Corrections by collection over the last ${DAYS} days. The collections at the top are where Reading goes wrong most.`}
        />
        {overview.correctionsByCollection.length === 0 ? (
          <EmptyState
            title="No corrections yet"
            description="Once reviewers or members correct a Takeoff, the collections they touch show here."
          />
        ) : (
          <div className="flex flex-col gap-y-3 px-6 py-4">
            {overview.correctionsByCollection.map((row) => (
              <div key={row.collection} className="flex flex-col gap-y-1">
                <div className="txt-compact-small flex items-center justify-between gap-x-4">
                  <span className="text-ui-fg-base">{row.collection}</span>
                  <span className="text-ui-fg-subtle">
                    {row.count} ({row.perRun} per run)
                  </span>
                </div>
                <ProgressBar value={mostCorrected === 0 ? 0 : (row.count / mostCorrected) * 100} />
              </div>
            ))}
            {overview.topCorrectedFields.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <span className="txt-compact-small text-ui-fg-subtle">Most corrected fields:</span>
                {overview.topCorrectedFields.map((field) => (
                  <Badge key={field.field}>
                    {field.field} {field.count}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Harness scores"
          description="Each reading scored against facts a person checked on the drawings, or against the Building Model a Reviewer approved, which nobody checked independently and is labelled so. The scoring command in docs/accuracy.md adds new readings here."
        />
        {scores.reports.length === 0 ? (
          <EmptyState
            icon={<ChartActivity />}
            title="No harness reports"
            description={`Nothing scored yet. Reports are read from ${scores.dir}.`}
          />
        ) : (
          scores.reports.map((report) => (
            <div key={report.id} className="flex flex-col gap-y-3 px-6 py-4">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <div className="min-w-0">
                  <span className="txt-compact-small-plus text-ui-fg-base">{report.planSet}</span>
                  <span className="txt-compact-small ml-2 text-ui-fg-subtle">
                    {report.projectName}, {report.runId},{" "}
                    {report.revision > 0 ? `revision ${report.revision}` : "a new reading"}
                  </span>
                </div>
                <span className="tabular text-[1.375rem] font-medium leading-7 text-ui-fg-base">
                  {report.overall}%
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {report.truthKind === "reviewed" ? (
                  <Badge color="purple">Truth: {report.truthLabel}</Badge>
                ) : null}
                {report.categories.map((category) => (
                  <StatusBadge key={category.category} color={scoreTone(category.score)}>
                    {categoryLabel[category.category] ?? category.category}{" "}
                    {Math.round(category.score * 100)}%
                  </StatusBadge>
                ))}
              </div>
              <div className="txt-compact-xsmall text-ui-fg-muted">
                Read by {report.readingModel ?? "unknown"}
                {report.readAt ? ` ${ago(report.readAt)}` : ""}
                {report.costCents === null ? "" : ` for ${money(report.costCents)}`}, scored{" "}
                {ago(report.scoredAt)}.
              </div>
              {report.misses.length > 0 ? (
                <details className="txt-small text-ui-fg-subtle">
                  <summary className="cursor-pointer text-ui-fg-base">
                    Largest misses ({report.misses.length})
                  </summary>
                  <ul className="mt-2 flex flex-col gap-y-1 pl-4">
                    {report.misses.map((miss) => (
                      <li key={`${miss.category}-${miss.label}`} className="list-disc">
                        {miss.label}: expected {miss.expected}, got {miss.actual} ({miss.grade},
                        from sheet {miss.sheet})
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ))
        )}
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Recent runs"
          description={`Every run read in the last ${RUN_DAYS} days. Lines compares the first Takeoff with the latest revision: a large difference means reviewers changed a lot. Open a run for its events and evidence check.`}
        />
        {runs.runs.length === 0 ? (
          <EmptyState title="No runs yet" description="Runs appear here once Reading finishes." />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Project</Table.HeaderCell>
                <Table.HeaderCell className="max-lg:hidden">Yard</Table.HeaderCell>
                <Table.HeaderCell className="max-sm:hidden">Read</Table.HeaderCell>
                <Table.HeaderCell>Cost</Table.HeaderCell>
                <Table.HeaderCell className="max-lg:hidden">Minutes</Table.HeaderCell>
                <Table.HeaderCell>Corrections</Table.HeaderCell>
                <Table.HeaderCell className="max-lg:hidden">Lines</Table.HeaderCell>
                <Table.HeaderCell className="max-sm:hidden">Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {runs.runs.map((run) => (
                <Table.Row
                  key={run.id}
                  data-clickable="true"
                  onClick={() => setSelected(selected === run.id ? null : run.id)}
                >
                  <Table.Cell className="txt-compact-small-plus max-w-[11rem] truncate whitespace-nowrap text-ui-fg-base sm:max-w-[16rem]">
                    {run.projectName}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-lg:hidden">
                    {run.yardName}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-sm:hidden">
                    {ago(run.createdAt)}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {run.costCents === null ? "-" : money(run.costCents)}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-lg:hidden">
                    {run.minutes ?? "-"}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{run.corrections}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-lg:hidden">
                    {run.firstLines === null
                      ? "-"
                      : run.lineDelta
                        ? `${run.firstLines} to ${run.latestLines} (${run.lineDelta > 0 ? "+" : ""}${run.lineDelta})`
                        : `${run.firstLines}`}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-sm:hidden">
                    <StatusBadge color={statusTone[run.status] ?? "grey"}>
                      {statusLabel[run.status] ?? run.status}
                    </StatusBadge>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      {selected ? <RunDetail id={selected} /> : null}
    </>
  );
}

interface EvidenceSummary {
  elements: number;
  evidence: number;
  dropped: number;
  remapped: number;
  clamped: number;
  duplicateIds: number;
  withoutEvidence: number;
}

function RunDetail({ id }: { id: string }) {
  const { data, isPending } = useQuery(accuracyRunQuery(id));

  if (isPending || !data) {
    return (
      <Container className="flex flex-col gap-y-3">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
      </Container>
    );
  }

  const evidence = data.evidenceCheck as EvidenceSummary | null;
  const sheetsRead = data.sheetsRead as number[] | null;
  const enhanced = data.enhanced as number[] | null;

  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title={`${data.run.projectName}`}
        description={`${data.run.yardName}, run ${data.run.id}, read by ${data.run.readingModel ?? "unknown"}${data.run.usage ? ` in ${data.run.usage.calls} calls for ${money(data.run.usage.costCents)}` : ""}.`}
      />
      <dl className="grid gap-x-8 gap-y-4 px-6 py-4 sm:grid-cols-2">
        <div>
          <dt className="txt-compact-small-plus text-ui-fg-base">Sheets read</dt>
          <dd className="txt-small mt-1 text-ui-fg-subtle">
            {sheetsRead
              ? `${sheetsRead.length} of ${data.run.sheetCount}: pages ${sheetsRead.join(", ")}`
              : "Not recorded for this run."}
            {enhanced && enhanced.length > 0
              ? ` ${enhanced.length} scanned sheet${enhanced.length === 1 ? "" : "s"} enhanced before reading.`
              : ""}
          </dd>
        </div>
        <div>
          <dt className="txt-compact-small-plus text-ui-fg-base">Evidence check</dt>
          <dd className="txt-small mt-1 text-ui-fg-subtle">
            {evidence
              ? `${evidence.evidence} entries on ${evidence.elements} elements: ${evidence.dropped} dropped, ${evidence.remapped} moved to the right sheet, ${evidence.clamped} regions clamped, ${evidence.duplicateIds} duplicate ids renamed, ${evidence.withoutEvidence} elements without evidence.`
              : "Not recorded: this run was read before the evidence check existed."}
          </dd>
        </div>
        <div>
          <dt className="txt-compact-small-plus text-ui-fg-base">Revisions</dt>
          <dd className="txt-small mt-1 text-ui-fg-subtle">
            {data.revisions
              .map(
                (revision) =>
                  `${revision.revision} (${revision.origin}, ${revision.lines} lines${revision.lowestConfidence === null ? "" : `, lowest confidence ${revision.lowestConfidence}`})`,
              )
              .join("; ")}
          </dd>
        </div>
        <div>
          <dt className="txt-compact-small-plus text-ui-fg-base">Corrections</dt>
          <dd className="txt-small mt-1 text-ui-fg-subtle">
            {data.corrections.length === 0
              ? "None."
              : data.corrections
                  .map(
                    (item) =>
                      `${item.collection} ${item.action}${item.fields.length > 0 ? ` (${item.fields.join(", ")})` : ""}, ${(reasonLabel[item.reason] ?? item.reason).toLowerCase()}`,
                  )
                  .join("; ")}
          </dd>
        </div>
      </dl>
      <div className="px-6 py-4">
        <div className="txt-compact-small-plus mb-2 text-ui-fg-base">Events</div>
        <ol className="flex flex-col gap-y-1">
          {data.events.map((event) => (
            <li key={event.id} className="txt-small flex gap-x-3 text-ui-fg-subtle">
              <span className="w-20 shrink-0 text-ui-fg-muted">{event.stage}</span>
              <span className={event.level === "warning" ? "text-ui-fg-error" : undefined}>
                {event.message}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </Container>
  );
}

/* ---- Confidence ------------------------------------------------------------- */

/** Whether Confidence works, over the Reviews of the same window as the agreement. Loads after the page. */
function ConfidenceSection() {
  const { data, isPending } = useQuery(accuracyConfidenceQuery(AGREEMENT_DAYS));
  if (isPending || !data) {
    return (
      <Container className="flex flex-col gap-y-3">
        <Skeleton className="h-5 w-1/4" />
        <Skeleton className="h-5 w-2/3" />
      </Container>
    );
  }
  return <ConfidenceReportSection report={data} />;
}

/* ---- Agreement with Reviewers ------------------------------------------------ */

const reasonLabel = Object.fromEntries(
  correctionReasons("reviewer").map((reason) => [reason.value, reason.label]),
) as Record<string, string>;

const agreementTone = (share: number | null): Tone =>
  share === null ? "grey" : share >= 0.9 ? "green" : share >= 0.7 ? "orange" : "red";

const percentChange = (value: number | null | undefined) =>
  value === null || value === undefined ? "-" : `${signed(value)}%`;

const weekFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
const weekLabel = (week: string) => `Week of ${weekFormat.format(new Date(`${week}T00:00:00Z`))}`;

const lineSummary = (lines: { added: number; removed: number; changed: number }) => {
  const parts = [
    lines.added > 0 ? `${lines.added} added` : null,
    lines.removed > 0 ? `${lines.removed} removed` : null,
    lines.changed > 0 ? `${lines.changed} changed` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(", ") : "None";
};

/** How far Reading was from what Reviewers approved, and how that moves week by week. */
function AgreementSection() {
  const { data } = useSuspenseQuery(agreementQuery(AGREEMENT_DAYS));
  const [selected, setSelected] = useState<string | null>(null);
  const { summary, trend, reviews, commonCorrections } = data;
  const latest = reviews.slice(0, 12);

  return (
    <>
      <Container className="divide-y p-0">
        <SectionHeader
          title="Agreement with Reviewers"
          description={`Every approved Review of a reading from the last ${AGREEMENT_DAYS} days, comparing the Takeoff Reading produced with the one the Reviewer approved. Agreement is the share of Materials the Reviewer left alone; one they added, removed or changed counts against Reading. Weeks are when the drawings were read, so a change to Reading, such as a new lesson, shows in the weeks after it.`}
        />
        <div className="grid grid-cols-2 gap-px bg-ui-border-base lg:grid-cols-4">
          <Stat
            className="bg-ui-bg-base"
            label="Median agreement"
            value={percent(summary.medianAgreement)}
            hint="Materials the Reviewer left alone"
          />
          <Stat
            className="bg-ui-bg-base"
            label="Approved untouched"
            value={percent(summary.untouchedShare)}
            hint="Reviews with no correction at all"
          />
          <Stat
            className="bg-ui-bg-base"
            label="Board feet change"
            value={percentChange(summary.medianBoardFeetChangePct)}
            hint="Median, approved against read"
          />
          <Stat
            className="bg-ui-bg-base"
            label="Reviews measured"
            value={summary.reviews}
            hint={`Approved, last ${AGREEMENT_DAYS} days`}
          />
        </div>
        {trend.length === 0 ? (
          <EmptyState
            icon={<ChartActivity />}
            title="No approved reviews yet"
            description="Once a Reviewer approves a reading, its agreement shows here, week by week."
          />
        ) : (
          <div className="flex flex-col gap-y-3 px-6 py-4">
            <div className="txt-compact-small-plus text-ui-fg-base">
              Median agreement by the week the drawings were read
            </div>
            {trend.map((week) => (
              <div key={week.week} className="flex flex-col gap-y-1">
                <div className="txt-compact-small flex flex-wrap items-center justify-between gap-x-4">
                  <span className="text-ui-fg-base">{weekLabel(week.week)}</span>
                  <span className="text-ui-fg-subtle">
                    {week.reviews === 0
                      ? "No approved reviews"
                      : `${percent(week.medianAgreement)}, ${week.reviews} review${week.reviews === 1 ? "" : "s"}, ${week.corrections} correction${week.corrections === 1 ? "" : "s"}`}
                  </span>
                </div>
                <ProgressBar
                  value={(week.medianAgreement ?? 0) * 100}
                  tone={week.reviews === 0 ? "neutral" : "interactive"}
                />
              </div>
            ))}
          </div>
        )}
      </Container>

      {latest.length > 0 ? (
        <Container className="divide-y p-0">
          <SectionHeader
            title="Latest approved reviews"
            description="Newest approval first. Open one for the Materials that moved and every Correction."
          />
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Project</Table.HeaderCell>
                <Table.HeaderCell className="max-sm:hidden">Read</Table.HeaderCell>
                <Table.HeaderCell>Agreement</Table.HeaderCell>
                <Table.HeaderCell className="max-lg:hidden">Materials</Table.HeaderCell>
                <Table.HeaderCell className="max-sm:hidden">Board feet</Table.HeaderCell>
                <Table.HeaderCell className="max-md:hidden">Corrections</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {latest.map((row) => (
                <Table.Row
                  key={row.reviewId}
                  data-clickable="true"
                  onClick={() => setSelected(selected === row.reviewId ? null : row.reviewId)}
                >
                  <Table.Cell className="txt-compact-small-plus max-w-[10rem] truncate whitespace-nowrap text-ui-fg-base sm:max-w-[16rem]">
                    {row.projectName}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-sm:hidden">
                    {ago(row.readAt)}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <StatusBadge color={agreementTone(row.agreement)}>
                      {percent(row.agreement)}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-lg:hidden">
                    {lineSummary(row.lines)} of {row.lines.compared}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-sm:hidden">
                    {percentChange(row.boardFeet.changePct)}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-md:hidden">
                    {row.corrections.total}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Container>
      ) : null}

      {selected ? <AgreementDetail id={selected} /> : null}

      <Container className="divide-y p-0">
        <SectionHeader
          title="Most common corrections this month"
          description="What Reviewers changed in the last 30 days, grouped by what changed and why. Repeated ones are candidates for a Reading Lesson."
        />
        {commonCorrections.length === 0 ? (
          <EmptyState
            title="No corrections this month"
            description="Corrections Reviewers make show here, the most frequent first."
          />
        ) : (
          <ul className="divide-y">
            {commonCorrections.map((item) => (
              <li key={`${item.label}-${item.reason}`} className="flex flex-col gap-y-1 px-6 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge color="blue">{item.count}</Badge>
                  <span className="txt-compact-small-plus break-all text-ui-fg-base">
                    {item.label}
                  </span>
                  <span className="txt-compact-small text-ui-fg-subtle">
                    {reasonLabel[item.reason] ?? item.reason}
                  </span>
                </div>
                {item.notes.map((note) => (
                  <p key={note} className="txt-small text-pretty break-words text-ui-fg-muted">
                    {note}
                  </p>
                ))}
              </li>
            ))}
          </ul>
        )}
      </Container>
    </>
  );
}

function AgreementDetail({ id }: { id: string }) {
  const { data, isPending } = useQuery(agreementReviewQuery(id));

  if (isPending || !data) {
    return (
      <Container className="flex flex-col gap-y-3">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-5 w-2/3" />
      </Container>
    );
  }

  const { review, agreement, lines, corrections } = data;
  const list = (title: string, items: string[]) => (
    <div>
      <dt className="txt-compact-small-plus text-ui-fg-base">{title}</dt>
      <dd className="txt-small mt-1 text-pretty break-words text-ui-fg-subtle">
        {items.length === 0 ? "None." : items.join("; ")}
      </dd>
    </div>
  );
  const described = (line: { description: string; orderQuantity: number; unit: string }) =>
    `${line.description}, ${quantity(line.orderQuantity)} ${line.unit}`;

  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title={review.projectName}
        description={`${review.yardName}. Read ${date(review.readAt)} by ${review.readingModel ?? "an unknown model"}, approved${review.approvedAt ? ` ${ago(review.approvedAt)}` : ""} by ${review.reviewerName ?? "a Reviewer"} as revision ${review.approvedRevision}, after ${review.minutesSpent} minutes. ${percent(agreement.agreement)} of ${agreement.lines.compared} Materials left alone; board feet ${quantity(agreement.boardFeet.reading)} read, ${quantity(agreement.boardFeet.approved)} approved.`}
      />
      <dl className="grid gap-x-8 gap-y-4 px-6 py-4 sm:grid-cols-2">
        {list("Added by the Reviewer", lines.added.map(described))}
        {list("Removed by the Reviewer", lines.removed.map(described))}
        {list(
          "Changed quantity",
          lines.changed.map(
            (line) =>
              `${line.description}, ${quantity(line.before)} to ${quantity(line.after)} ${line.unit}`,
          ),
        )}
        {list(
          "Corrections by reason",
          agreement.corrections.byReason.map(
            (item) => `${reasonLabel[item.reason] ?? item.reason} ${item.count}`,
          ),
        )}
      </dl>
      {corrections.length > 0 ? (
        <ol className="flex flex-col gap-y-2 px-6 py-4">
          {corrections.map((item) => (
            <li key={item.id} className="txt-small flex flex-col gap-y-0.5">
              <span className="break-words text-ui-fg-base">
                {item.collection} {item.action}
                {item.fields.length > 0 ? `: ${item.fields.join(", ")}` : ""},{" "}
                {(reasonLabel[item.reason] ?? item.reason).toLowerCase()}, by{" "}
                {item.userName ?? item.by}
              </span>
              {item.note ? (
                <span className="text-pretty break-words text-ui-fg-muted">{item.note}</span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
    </Container>
  );
}
